import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlarmClock, ArrowRight, BellRing, CalendarClock, CircleAlert, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isEEOCEligible, useEEOCCycle } from '../../hooks/useEEOCCycle';
import { timeAgo } from '../quality/qualityConfig';

export const formatDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-');

const TONES = {
    completed: { Icon: ShieldCheck, box: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200', icon: 'text-emerald-600' },
    missed: { Icon: CircleAlert, box: 'border-red-300 bg-red-50 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200', icon: 'text-red-600 animate-pulse' },
    due_soon: { Icon: AlarmClock, box: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200', icon: 'text-amber-600 animate-pulse' },
    due: { Icon: CalendarClock, box: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200', icon: 'text-sky-600' }
};

const message = (c, tone) => {
    const progress = <><b>{c.modules_passed}/{c.total_modules}</b> modules passed this {c.unit}</>;
    if (tone === 'completed') return <>You completed the <b>{c.label}</b> EEOC refresher on {formatDay(c.completed_at)}. The next one starts after {formatDay(c.due_date)}.</>;
    if (tone === 'missed') return <>You missed the <b>{c.previous.label}</b> EEOC refresher. Pass all {c.total_modules} modules by <b>{formatDay(c.due_date)}</b> to get back on track. {progress}.</>;
    if (tone === 'due_soon') return <>Your <b>{c.label}</b> EEOC refresher is due in <b>{c.days_left} day{c.days_left === 1 ? '' : 's'}</b> ({formatDay(c.due_date)}). {progress}.</>;
    return <>EEOC refresher for <b>{c.label}</b>: pass all {c.total_modules} modules by <b>{formatDay(c.due_date)}</b>. {progress}.</>;
};

export const EEOCCycleBanner = ({ cycle, showLink = false }) => {
    if (!cycle) return null;
    const reminder = !cycle.completed && cycle.reminder?.last_nudged_at ? cycle.reminder : null;
    const tone = cycle.completed ? 'completed' : cycle.previous?.missed || reminder ? 'missed' : cycle.status;
    const { Icon, box, icon } = TONES[tone];
    return (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${box}`}
            data-testid="eeoc-cycle-banner" data-tone={tone}>
            <Icon className={`h-5 w-5 shrink-0 ${icon}`} />
            <p className="min-w-0 flex-1 text-sm" data-testid="eeoc-cycle-banner-text">{message(cycle, tone)}</p>
            {reminder && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:text-red-300" data-testid="eeoc-cycle-banner-reminder">
                    <BellRing className="h-3.5 w-3.5" /> Reminder from {reminder.last_nudged_by_name} · {timeAgo(reminder.last_nudged_at)}
                </span>
            )}
            {showLink && (
                <Link to="/eeoc-training" className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:underline dark:text-sky-300" data-testid="eeoc-cycle-banner-link">
                    Open EEOC Training <ArrowRight className="h-4 w-4" />
                </Link>
            )}
        </motion.div>
    );
};

export const EEOCDueBanner = () => {
    const { user, isAdmin } = useAuth();
    const cycle = useEEOCCycle(isEEOCEligible(user, isAdmin));
    return cycle && !cycle.completed ? <EEOCCycleBanner cycle={cycle} showLink /> : null;
};
